import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { FolderOpen, FileText, Lock, Eye, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEVEL_CONFIG = {
  1: { label: 'Level 1 - Care', icon: Eye, color: 'text-[#00A6A0]', bg: 'bg-[#00A6A0]/10', desc: 'Visible to caregivers' },
  2: { label: 'Level 2 - Confidential', icon: Lock, color: 'text-amber-400', bg: 'bg-amber-400/10', desc: 'Admin only' },
};

export default function ClientDocuments() {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewLevel, setViewLevel] = useState('all');

  useEffect(() => {
    axios.get(`${API_URL}/api/client-portal/documents`, { withCredentials: true })
      .then(res => setDocs(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const level1 = docs.filter(d => d.folder_level === 1 || !d.folder_level);
  const level2 = docs.filter(d => d.folder_level === 2);
  const filtered = viewLevel === 'all' ? docs : viewLevel === '1' ? level1 : level2;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="client-documents-page">
        <div>
          <h1 className="text-2xl font-heading font-bold text-foreground">My Documents</h1>
          <p className="text-sm text-muted-foreground mt-1">Care plans, contracts, and correspondence</p>
        </div>

        {/* Level stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="glass-card border-border cursor-pointer" onClick={() => setViewLevel('all')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-foreground">{docs.length}</p>
              <p className="text-xs text-muted-foreground">All Documents</p>
            </CardContent>
          </Card>
          <Card className={`glass-card border-border cursor-pointer ${viewLevel === '1' ? 'border-[#00A6A0]' : ''}`}
            onClick={() => setViewLevel(viewLevel === '1' ? 'all' : '1')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-[#00A6A0]">{level1.length}</p>
              <p className="text-xs text-muted-foreground">Level 1 - Care</p>
            </CardContent>
          </Card>
          <Card className={`glass-card border-border cursor-pointer ${viewLevel === '2' ? 'border-amber-400' : ''}`}
            onClick={() => setViewLevel(viewLevel === '2' ? 'all' : '2')}>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-400">{level2.length}</p>
              <p className="text-xs text-muted-foreground">Level 2 - Confidential</p>
            </CardContent>
          </Card>
        </div>

        {/* Documents List */}
        <Card className="glass-card border-border">
          <CardHeader className="border-b border-border">
            <CardTitle className="text-lg font-heading text-foreground flex items-center gap-2">
              <FolderOpen className="w-5 h-5 text-[#FF6F61]" /> Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="p-8 text-center">
                <FileText className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No documents found</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {filtered.map((doc, i) => {
                  const level = doc.folder_level || 1;
                  const cfg = LEVEL_CONFIG[level] || LEVEL_CONFIG[1];
                  const LevelIcon = cfg.icon;
                  return (
                    <div key={doc.id || i} className="p-4 flex items-center gap-4 hover:bg-muted/30 transition-colors"
                      data-testid={`doc-${doc.id || i}`}>
                      <div className={`p-2 rounded-lg ${cfg.bg} flex-shrink-0`}>
                        <LevelIcon className={`w-5 h-5 ${cfg.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-foreground font-medium text-sm">{doc.title || doc.name || 'Document'}</p>
                        <p className="text-xs text-muted-foreground">
                          {doc.category || 'General'} &bull; {cfg.desc}
                        </p>
                        {doc.content && (
                          <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{doc.content}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <span className={`text-[10px] px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.color}`}>
                          {cfg.label}
                        </span>
                        {doc.created_at && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {new Date(doc.created_at).toLocaleDateString()}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
